// Parte de viajeros para SES.Hospedajes (Ministerio del Interior, España) a partir de
// DNI y pasaportes extraídos por WhappScan (30-9-2026).
//
// Se genera el XML que el anfitrión sube en «Alta masiva» de la aplicación web
// (hospedajes.ses.mir.es): sin credenciales ni servicio web por nuestra parte. Formato
// según «Instrucciones para el alta masiva de comunicaciones v1.1.0» (08-01-2024) y el
// manual del servicio web v3.1.2 (17-09-2024), ambos del Ministerio del Interior.
//
//   <alt:peticion xmlns:alt="http://www.neg.hospedajes.mir.es/altaParteHospedaje">
//     <solicitud>
//       <codigoEstablecimiento/> <comunicacion> <contrato/> <persona/>+ </comunicacion>
//
// Los elementos hijos NO llevan espacio de nombres y el ORDEN importa (xsd:sequence).
// Reglas de obligatoriedad (manual, apartado 3): por huésped, domicilio completo (también
// menores); teléfono, teléfono2 o correo; tipo y número de documento si es mayor de edad;
// soporte para NIF/NIE; apellido2 para NIF; parentesco en el menor.
//
// Lo que NO se ha podido comprobar (los XSD oficiales están tras login): que el portal
// acepte exactamente esta salida. Se validó la estructura con el XSD de la comunidad v3.0.0.

export const MAX_GUESTS = 100;

const NS = "http://www.neg.hospedajes.mir.es/altaParteHospedaje";
const PARTICLES = new Set(["DE", "DEL", "LA", "LAS", "LOS", "Y", "DA", "DO", "DOS", "DAS", "VAN", "VON", "SAN", "SANTA", "MAC", "MC"]);
const NIF_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";

export function xmlEscape(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;")
    // El XML 1.0 no admite caracteres de control.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "");
}

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function nifLetterOk(value) {
  const m = String(value || "").toUpperCase().match(/^(\d{8})([A-Z])$/);
  return Boolean(m) && NIF_LETTERS[Number(m[1]) % 23] === m[2];
}

export function nieLetterOk(value) {
  const m = String(value || "").toUpperCase().match(/^([XYZ])(\d{7})([A-Z])$/);
  return Boolean(m) && NIF_LETTERS[Number(`${"XYZ".indexOf(m[1])}${m[2]}`) % 23] === m[3];
}

// «DE LA CRUZ SANCHEZ» → ["DE LA CRUZ", "SANCHEZ"]. Las partículas se pegan a la palabra
// siguiente. Es una heurística: el anfitrión puede corregirla en pantalla.
export function splitSurnames(surname) {
  const tokens = String(surname || "").trim().split(/\s+/).filter(Boolean);
  const groups = [];
  let cur = [];
  for (const t of tokens) {
    cur.push(t);
    if (!PARTICLES.has(t.toUpperCase())) {
      groups.push(cur.join(" "));
      cur = [];
    }
  }
  if (cur.length) {
    if (groups.length) groups[groups.length - 1] += ` ${cur.join(" ")}`;
    else groups.push(cur.join(" "));
  }
  return { apellido1: groups[0] || "", apellido2: groups.slice(1).join(" ") };
}

export function ageAt(birthIso, atIso) {
  const b = String(birthIso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  const a = String(atIso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!b || !a) return null;
  let age = Number(a[1]) - Number(b[1]);
  if (Number(a[2]) < Number(b[2]) || (Number(a[2]) === Number(b[2]) && Number(a[3]) < Number(b[3]))) age -= 1;
  return age;
}

const SEX = { M: "H", H: "H", F: "M", X: "O", O: "O" };

const field = (fields, name) => {
  const f = (fields || []).find((x) => x?.name === name);
  const v = f?.value;
  return v === null || v === undefined || v === "" ? null : String(v).trim();
};

function tipoDocumento(fields, number) {
  const type = String(field(fields, "document_type") || "").toLowerCase();
  const issuer = String(field(fields, "issuing_country") || "").toUpperCase();
  const num = String(number || "").toUpperCase();
  if (type === "passport") return "PAS";
  if (/^[XYZ]\d{7}[A-Z]$/.test(num)) return "NIE";
  if (type === "residence_permit" && issuer === "ESP") return "NIE";
  if (type === "id_card" && issuer === "ESP" && /^\d{8}[A-Z]$/.test(num)) return "NIF";
  return "OTRO";
}

// Datos del documento → persona de SES (sin domicilio ni contacto, que pone el anfitrión).
// `overrides`: correcciones hechas en pantalla (mismas claves que el resultado).
export function personFromDocument(fields, overrides = {}, arrivalIso) {
  const ov = overrides;
  const number = String(ov.numeroDocumento ?? field(fields, "document_number") ?? "")
    .toUpperCase()
    .replace(/[\s-]/g, "");
  // Sin número de documento (un menor sin él) no hay tipo: SES no lo admite suelto.
  const tipo = number ? ov.tipoDocumento || tipoDocumento(fields, number) : ov.tipoDocumento || "";
  const surname = field(fields, "surname") || "";
  const split = tipo === "NIF" || String(field(fields, "issuing_country") || "").toUpperCase() === "ESP" ? splitSurnames(surname) : { apellido1: surname, apellido2: "" };
  const birth = ov.fechaNacimiento ?? (field(fields, "birth_date") || "").slice(0, 10);
  const nationality = (ov.nacionalidad ?? field(fields, "nationality_code") ?? "").toUpperCase();
  const sexRaw = String(ov.sexo ?? field(fields, "sex") ?? "").toUpperCase();
  return {
    nombre: ov.nombre ?? field(fields, "given_names") ?? "",
    apellido1: ov.apellido1 ?? split.apellido1,
    apellido2: ov.apellido2 ?? split.apellido2,
    tipoDocumento: tipo,
    numeroDocumento: number,
    soporteDocumento: String(ov.soporteDocumento ?? field(fields, "support_number") ?? "").toUpperCase().replace(/\s/g, ""),
    fechaNacimiento: /^\d{4}-\d{2}-\d{2}$/.test(birth) ? birth : "",
    nacionalidad: nationality,
    sexo: SEX[sexRaw] || (["H", "M", "O"].includes(sexRaw) ? sexRaw : ""),
    menor: (() => {
      const age = ageAt(birth, arrivalIso);
      return age !== null && age < 18;
    })(),
  };
}

const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));
const isDateTime = (v) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(String(v || ""));
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || ""));

// Devuelve { contract: [claves], guests: [[claves]] } con lo que impide generar el fichero.
export function validateSes({ codigoEstablecimiento, contrato, personas }) {
  const contract = [];
  if (!/^[A-Za-z0-9]{1,10}$/.test(String(codigoEstablecimiento || "").trim())) contract.push("codigoEstablecimiento");
  if (!String(contrato.referencia || "").trim()) contract.push("referencia");
  if (!isDate(contrato.fechaContrato)) contract.push("fechaContrato");
  if (!isDateTime(contrato.fechaEntrada)) contract.push("fechaEntrada");
  if (!isDateTime(contrato.fechaSalida)) contract.push("fechaSalida");
  else if (isDateTime(contrato.fechaEntrada) && contrato.fechaSalida <= contrato.fechaEntrada) contract.push("fechaSalida");
  if (!contrato.tipoPago) contract.push("tipoPago");
  if (personas.length === 0 || personas.length > MAX_GUESTS) contract.push("personas");

  const hasMinor = personas.some((p) => p.menor);
  const guests = personas.map((p) => {
    const e = [];
    if (!String(p.nombre || "").trim()) e.push("nombre");
    if (!String(p.apellido1 || "").trim()) e.push("apellido1");
    if (p.tipoDocumento === "NIF" && !String(p.apellido2 || "").trim()) e.push("apellido2");
    if (!isDate(p.fechaNacimiento)) e.push("fechaNacimiento");
    if (!p.menor) {
      if (!p.tipoDocumento) e.push("tipoDocumento");
      if (!p.numeroDocumento) e.push("numeroDocumento");
    }
    if ((p.tipoDocumento === "NIF" || p.tipoDocumento === "NIE") && p.numeroDocumento) {
      if (!String(p.soporteDocumento || "").trim()) e.push("soporteDocumento");
      const ok = p.tipoDocumento === "NIF" ? nifLetterOk(p.numeroDocumento) : nieLetterOk(p.numeroDocumento);
      if (!ok) e.push("numeroDocumento");
    }
    if (p.numeroDocumento && p.numeroDocumento.length > 15) e.push("numeroDocumento");
    const d = p.direccion || {};
    if (!String(d.direccion || "").trim()) e.push("direccion");
    if (!String(d.codigoPostal || "").trim()) e.push("codigoPostal");
    if (!/^[A-Z]{3}$/.test(String(d.pais || ""))) e.push("pais");
    else if (d.pais === "ESP" && !/^\d{5}$/.test(String(d.codigoMunicipio || ""))) e.push("municipio");
    else if (d.pais !== "ESP" && !String(d.nombreMunicipio || "").trim()) e.push("municipio");
    const hasPhone = String(p.telefono || "").trim() || String(p.telefono2 || "").trim();
    const hasMail = String(p.correo || "").trim();
    if (!hasPhone && !hasMail) e.push("contacto");
    if (hasMail && !isEmail(p.correo)) e.push("correo");
    if (p.menor && hasMinor && !p.parentesco) e.push("parentesco");
    return e;
  });
  return { contract, guests, ok: contract.length === 0 && guests.every((g) => g.length === 0) };
}

const el = (name, value, indent) =>
  value === null || value === undefined || String(value).trim() === "" ? "" : `${indent}<${name}>${xmlEscape(String(value).trim())}</${name}>\n`;

const withSeconds = (v) => (/T\d{2}:\d{2}$/.test(String(v)) ? `${v}:00` : String(v));

export function buildSesXml({ codigoEstablecimiento, contrato, personas }) {
  const c = contrato;
  let x = `<?xml version="1.0" encoding="UTF-8"?>\n<alt:peticion xmlns:alt="${NS}">\n  <solicitud>\n`;
  x += el("codigoEstablecimiento", String(codigoEstablecimiento).trim(), "    ");
  x += "    <comunicacion>\n      <contrato>\n";
  x += el("referencia", c.referencia, "        ");
  x += el("fechaContrato", c.fechaContrato, "        ");
  x += el("fechaEntrada", withSeconds(c.fechaEntrada), "        ");
  x += el("fechaSalida", withSeconds(c.fechaSalida), "        ");
  x += el("numPersonas", personas.length, "        ");
  x += el("numHabitaciones", c.numHabitaciones, "        ");
  x += el("internet", c.internet === true || c.internet === false ? String(c.internet) : "", "        ");
  x += "        <pago>\n";
  x += el("tipoPago", c.tipoPago, "          ");
  x += el("fechaPago", c.fechaPago, "          ");
  x += "        </pago>\n      </contrato>\n";
  for (const p of personas) {
    const d = p.direccion || {};
    x += "      <persona>\n        <rol>VI</rol>\n";
    x += el("nombre", p.nombre, "        ");
    x += el("apellido1", p.apellido1, "        ");
    x += el("apellido2", p.apellido2, "        ");
    x += el("tipoDocumento", p.tipoDocumento, "        ");
    x += el("numeroDocumento", p.numeroDocumento, "        ");
    x += el("soporteDocumento", p.soporteDocumento, "        ");
    x += el("fechaNacimiento", p.fechaNacimiento, "        ");
    x += el("nacionalidad", p.nacionalidad, "        ");
    x += el("sexo", p.sexo, "        ");
    x += "        <direccion>\n";
    x += el("direccion", d.direccion, "          ");
    x += el("direccionComplementaria", d.direccionComplementaria, "          ");
    x += el("codigoMunicipio", d.pais === "ESP" ? d.codigoMunicipio : "", "          ");
    x += el("nombreMunicipio", d.pais === "ESP" ? "" : d.nombreMunicipio, "          ");
    x += el("codigoPostal", d.codigoPostal, "          ");
    x += el("pais", d.pais, "          ");
    x += "        </direccion>\n";
    x += el("telefono", p.telefono, "        ");
    x += el("telefono2", p.telefono2, "        ");
    x += el("correo", p.correo, "        ");
    x += el("parentesco", p.menor ? p.parentesco : "", "        ");
    x += "      </persona>\n";
  }
  x += "    </comunicacion>\n  </solicitud>\n</alt:peticion>\n";
  return x;
}

// Búsqueda de municipios INE por nombre (sin tildes), primero los que empiezan por el texto.
export function searchMunicipios(list, query, limit = 8) {
  const q = normalizeText(query);
  if (q.length < 2) return [];
  const starts = [];
  const contains = [];
  for (const [code, name] of list) {
    const n = normalizeText(name);
    if (n.startsWith(q)) starts.push([code, name]);
    else if (n.includes(q)) contains.push([code, name]);
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}
